import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import pspricesDealsService, { siteImage } from "../service/pspricesDeals.service";
import { CARD_REGION } from "../components/nintendoDeals/useNintendoCards";
import { useFavorites } from "./FavoritesContext";
import { FavoriteBuyNintendo, FavoriteBuyPs, FavoriteBuySteam } from "./FavoriteBuy";
import { PLATFORM_LABELS, REGION_LABELS, keyOf } from "./favoriteKey";
import { useFavoritePrices } from "./useFavoritePrices";
import { PurchaseSourceOverride } from "../utils/metrika";

export const FAVORITES_TITLE_ID = "account-favorites-title";

const BUY_LABEL = { nintendo: "Купить карты", playstation: "Купить карты", steam: "Пополнить Steam" };

function money(value, currency) {
  try {
    return new Intl.NumberFormat("ru-RU", {
      style: "currency",
      currency,
      currencyDisplay: currency === "KZT" ? "narrowSymbol" : "symbol",
    }).format(value);
  } catch {
    return `${value} ${currency}`;
  }
}

const rub = (value) => `${Math.round(value).toLocaleString("ru-RU")} ₽`;
const dateRu = (value) =>
  new Date(value).toLocaleDateString("ru", { day: "numeric", month: "long" });

/** Цены витрины PlayStation по регионам избранного: Map "<region>|<id>" → карточка. */
function usePsShowcase(items) {
  const regions = useMemo(
    () =>
      [...new Set(items.filter((item) => item.platform === "playstation").map((item) => item.region))]
        .filter(Boolean)
        .sort(),
    [items],
  );
  const regionsKey = regions.join(",");
  const [deals, setDeals] = useState(new Map());
  useEffect(() => {
    if (!regionsKey) return undefined;
    let alive = true;
    Promise.all(
      regionsKey.split(",").map((region) =>
        pspricesDealsService
          .getDeals(region, "PS5")
          .then((data) => data.results.map((deal) => [`${region}|ps:${deal.id}`, deal]))
          .catch(() => []),
      ),
    ).then((lists) => alive && setDeals(new Map(lists.flat())));
    return () => {
      alive = false;
    };
  }, [regionsKey]);
  return deals;
}

/** Регион покупки Nintendo: регионы с ценой и картами; выбранный — сохранённый или самый выгодный. */
function nintendoRegions(item, price) {
  const regions = (price?.regions || []).filter((offer) => offer.cards && CARD_REGION[offer.region]);
  const region = item.region || price?.region || regions[0]?.region || null;
  return { regions, region };
}

function savedPrice(item) {
  return item.price != null && item.currency
    ? {
        price: item.price,
        original: item.original ?? null,
        percent: item.percent ?? null,
        currency: item.currency,
        nsuid: item.nsuid,
      }
    : null;
}

/** Текущая цена записи и признак, что это сохранённая цена. */
function currentPrice(item, price, psDeal, region) {
  if (item.platform === "playstation") {
    if (psDeal?.amount != null) {
      return {
        current: {
          price: psDeal.amount,
          original: psDeal.originalAmount,
          percent: psDeal.discountPercent,
          endsAt: psDeal.discountEndDate,
          currency: psDeal.currency,
        },
        stale: false,
      };
    }
    return { current: savedPrice(item), stale: true };
  }
  if (item.platform === "nintendo" && price?.regions) {
    const offer = price.regions.find((other) => other.region === region);
    if (offer) return { current: offer, stale: false };
  } else if (price?.price != null) {
    return { current: price, stale: false };
  }
  return { current: item.region === region ? savedPrice(item) : null, stale: true };
}

function discountPercent({ price, original, percent }) {
  if (!(original > price)) return null;
  return percent || Math.round((1 - price / original) * 100) || null;
}

function PriceInfo({ current, stale, item }) {
  if (!current) return <span className="text-gray-400">цены пока нет</span>;
  const percent = discountPercent(current);
  return (
    <span className="flex flex-wrap items-baseline gap-x-1.5 gap-y-1">
      <b className="text-gray-900 dark:text-white">{money(current.price, current.currency)}</b>
      {percent ? (
        <span className="text-xs text-gray-400 line-through">
          {money(current.original, current.currency)}
        </span>
      ) : null}
      {current.rub && current.currency !== "RUB" ? (
        <span className="text-xs font-medium text-indigo-700 dark:text-indigo-300">
          ≈ {rub(current.rub)}
        </span>
      ) : null}
      {percent ? (
        <span className="account-favorite-sale">
          {stale ? `скидка −${percent}%` : `сейчас со скидкой −${percent}%`}
        </span>
      ) : null}
      {stale && (item.priceAt || item.addedAt) ? (
        <span className="text-xs text-gray-500 dark:text-gray-400">
          цена на {dateRu(item.priceAt || item.addedAt)}
        </span>
      ) : null}
    </span>
  );
}

function FavoriteTitle({ item }) {
  const className = "font-medium text-gray-900 hover:underline dark:text-white";
  if (item.seoPath) {
    return (
      <a href={item.seoPath} className={className}>
        {item.title}
      </a>
    );
  }
  if (item.url) {
    return (
      <a href={item.url} target="_blank" rel="noreferrer" className={className}>
        {item.title}
      </a>
    );
  }
  return <span className={className}>{item.title}</span>;
}

function FavoriteCover({ image }) {
  const [broken, setBroken] = useState(false);
  return (
    <div className="account-favorite-cover">
      {image && !broken ? (
        <img src={siteImage(image)} alt="" loading="lazy" onError={() => setBroken(true)} />
      ) : null}
    </div>
  );
}

function FavoriteRow({ item, price, psDeal, open, onToggle }) {
  const { save, remove } = useFavorites();
  const nintendo = item.platform === "nintendo" ? nintendoRegions(item, price) : null;
  const region = nintendo ? nintendo.region : item.region;
  const { current, stale } = currentPrice(item, price, psDeal, region);
  const regionLabel = REGION_LABELS[item.platform]?.[region] || region;
  const regionOptions = nintendo
    ? nintendo.regions.some((offer) => offer.region === region) || !region
      ? nintendo.regions
      : [{ region }, ...nintendo.regions]
    : [];

  const chooseRegion = (code) => {
    const offer = nintendo.regions.find((other) => other.region === code);
    save({
      ...item,
      region: code,
      ...(offer
        ? {
            currency: offer.currency,
            price: offer.price,
            original: offer.original ?? null,
            percent: offer.percent ?? null,
            nsuid: offer.nsuid || item.nsuid,
          }
        : {}),
    });
  };

  return (
    <li className="account-favorite">
      <div className="flex">
        <FavoriteCover image={item.image} />
        <div className="min-w-0 flex-1 p-3 text-sm sm:px-4">
          <FavoriteTitle item={item} />
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-gray-500 dark:text-gray-400">
            <span>{PLATFORM_LABELS[item.platform]}</span>
            {regionOptions.length > 1 ? (
              <label className="inline-flex items-center gap-1">
                <span className="sr-only">Регион</span>
                <select
                  value={region || ""}
                  onChange={(event) => chooseRegion(event.target.value)}
                  className="rounded border border-gray-300 bg-white px-1 py-0.5 text-xs text-gray-800 dark:border-gray-600 dark:bg-[#222] dark:text-gray-100"
                >
                  {regionOptions.map((offer) => (
                    <option key={offer.region} value={offer.region}>
                      {REGION_LABELS.nintendo[offer.region] || offer.region}
                      {offer.rub ? ` — ≈ ${rub(offer.rub)}` : ""}
                    </option>
                  ))}
                </select>
              </label>
            ) : regionLabel ? (
              <span>· {regionLabel}</span>
            ) : null}
          </div>
          <div className="mt-1">
            <PriceInfo current={current} stale={stale} item={item} />
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <button type="button" className="account-favorite-buy" onClick={onToggle}>
              {open ? "Скрыть" : BUY_LABEL[item.platform]}
            </button>
            <button type="button" className="account-link-btn" onClick={() => remove(item)}>
              Убрать
            </button>
          </div>
        </div>
      </div>
      {open ? (
        <PurchaseSourceOverride value="favorites">
          <div className="px-3 pb-3 sm:px-4">
            {item.platform === "nintendo" ? (
              <FavoriteBuyNintendo item={item} region={region} offer={current} />
            ) : item.platform === "playstation" ? (
              <FavoriteBuyPs
                item={item}
                current={current}
                staleNote={
                  stale && current
                    ? `Цена на ${dateRu(item.priceAt || item.addedAt)} — перед покупкой проверьте её в PlayStation Store`
                    : null
                }
              />
            ) : (
              <FavoriteBuySteam item={item} current={current} />
            )}
          </div>
        </PurchaseSourceOverride>
      ) : null}
    </li>
  );
}

/** Раздел «Избранное» в кабинете: цены, скидки и покупка под игру без перехода на витрины. */
function AccountFavorites() {
  const { items, ready } = useFavorites();
  const { prices: fresh } = useFavoritePrices(items);
  const psDeals = usePsShowcase(items);
  // при смене региона Nintendo новый запрос ещё идёт — регионы из прошлого ответа те же
  const lastRegions = useRef({});
  const prices = useMemo(() => {
    const merged = { ...fresh };
    Object.entries(fresh).forEach(([key, value]) => {
      if (value?.regions) lastRegions.current[key] = value;
      else if (!value && lastRegions.current[key]) merged[key] = lastRegions.current[key];
    });
    return merged;
  }, [fresh]);
  const [openKey, setOpenKey] = useState(null);

  return (
    <section className="account-favorites" aria-labelledby={FAVORITES_TITLE_ID}>
      <h2
        id={FAVORITES_TITLE_ID}
        tabIndex={-1}
        className="account-favorites-title mb-3 text-lg font-semibold dark:text-white"
      >
        Избранные игры
      </h2>
      {!ready ? (
        <p className="other-store-description">Загрузка…</p>
      ) : !items.length ? (
        <p className="other-store-description">
          Отмечайте игры сердечком на страницах скидок:{" "}
          <Link to="/nintendo-discount" className="account-link-btn">
            Nintendo
          </Link>
          ,{" "}
          <Link to="/steam-discount" className="account-link-btn">
            Steam
          </Link>
          ,{" "}
          <Link to="/playstation-discount" className="account-link-btn">
            PlayStation
          </Link>
          .
        </p>
      ) : (
        <ul className="space-y-3">
          {items.map((item) => {
            const key = keyOf(item);
            return (
              <FavoriteRow
                key={key}
                item={item}
                price={prices[key]}
                psDeal={psDeals.get(`${item.region}|${item.gameKey}`)}
                open={openKey === key}
                onToggle={() => setOpenKey((current) => (current === key ? null : key))}
              />
            );
          })}
        </ul>
      )}
    </section>
  );
}

export default AccountFavorites;
